import { ref, type Ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import {
    AddressShopware, type Document, DocumentShopware,
    LoginRegistrationShopware, NewsletterShopware, OpenAPI,
    OrderShopware, ProfileShopware,
    SystemContextShopware
} from '@hubblecommerce/hubble/platforms/shopware/api-client'
import {
    useCart,
    useWishlist,
    useNotification,
    usePlatform,
    useRuntimeConfig,
    hblMapCustomer,
    hblMapCustomerAddress,
    hblMapCustomerAddresses,
    hblMapOrder,
    hblMapOrders,
    swMapCustomerAddress,
    useLocalisation,
    useCookie
} from '#imports'
import type {
    HblCustomer,
    HblCustomerShippingAddress,
    HblCustomerBillingAddress,
    HblIUseCustomer,
    HblRegisterCustomerForm,
    HblOrder
} from '@/utils/types'
import {request as __request} from "@hubblecommerce/hubble/platforms/shopware/request";

export const useCustomer = defineStore('use-customer', (): HblIUseCustomer => {
    const customer: Ref<HblCustomer | null> = ref(null)
    const loading: Ref<boolean> = ref(false)
    const error: Ref = ref(false)
    const { setSessionToken, getSession } = usePlatform()
    const { showNotification } = useNotification()
    const runtimeConfig = useRuntimeConfig()
    const { navigateToI18n } = useLocalisation()
    const { getCart } = useCart()
    const { getWishlist, clearWishlist } = useWishlist()

    // Set cookie if user is logged in to differ between session isset (context-token exists) and session
    // is related to a customer
    // @ts-ignore
    if (import.meta.client) {
        watch(customer, (newVal) => {
            const customerCookie = runtimeConfig.public.customerCookie as { name: string, options: any }
            const cookie = useCookie(customerCookie.name, customerCookie.options)

            if (newVal != null) {
                if (cookie.value !== '1') {
                    cookie.value = '1'
                }
            } else {
                cookie.value = null
            }
        })
    }

    async function getCustomer (): Promise<HblCustomer> {
        loading.value = true
        error.value = false

        try {
            const { sessionCookie } = useRuntimeConfig().public as { sessionCookie: { name: string, options: any } }
            const cookie = useCookie(sessionCookie.name)
            setSessionToken(cookie.value)

            /*
             * Get customer from context instead of ProfileShopware.readCustomer
             * because activeShippingAddress and activeBillingAddress is only available in /context
             * active addresses in session are mandatory for checkout contact step
             */
            const response = await SystemContextShopware.readContext()
            const mappedData = hblMapCustomer(response.customer)

            // Only set customer data client side to prevent leaked states on server
            customer.value = mappedData

            loading.value = false
            return mappedData
        } catch (e) {
            // Reset customer data in case of any error
            customer.value = null

            loading.value = false
            error.value = e
            throw e
        }
    }

    async function login (username: string, password: string): Promise<void> {
        loading.value = true
        error.value = false

        try {
            const platformStore = usePlatform()
            const { session } = storeToRefs(platformStore)

            // @ts-ignore
            if (session?.value?.sessionToken === null) {
                await getCart()
            }

            await LoginRegistrationShopware.loginCustomer({ username, password })
            loading.value = false

            await getSession()

            const wishlistStore = useWishlist()
            const { miniWishlist } = storeToRefs(wishlistStore)
            const { mergeWishlist } = wishlistStore
            if (miniWishlist?.value?.length > 0) {
                await mergeWishlist(miniWishlist?.value)
            }
            await getWishlist()

            return
        } catch (e) {
            const error = e as any

            if (error.body?.[0]?.detail != null) {
                showNotification(error.body[0]?.detail, 'error', true)
            } else {
                showNotification(error, 'error', true)
            }

            loading.value = false
            error.value = e
            throw e
        }
    }

    async function logout (): Promise<void> {
        loading.value = true
        error.value = false

        try {
            await LoginRegistrationShopware.logoutCustomer()
            loading.value = false

            customer.value = null
            await setSessionToken(null)
            await getSession()
            await getCart()
            clearWishlist()

            await navigateToI18n('/customer/logout')
            return
        } catch (e) {
            const error = e as any

            if (error.body[0]?.detail != null) {
                showNotification(error.body[0]?.detail, 'error', true)
            } else {
                showNotification(error, 'error', true)
            }

            loading.value = false
            error.value = e
            throw e
        }
    }

    async function logoutGuest (): Promise<void> {
        customer.value = null
        await setSessionToken(null)
        await getSession({ force: true })

        await getCart()
        clearWishlist()

        await navigateToI18n('/customer/login')
        return
    }

    async function register (formData: HblRegisterCustomerForm): Promise<HblCustomer | void> {
        loading.value = true
        error.value = false

        let dobObject = null
        if (formData.dateOfBirth != null && formData.dateOfBirth !== '') {
            dobObject = new Date(formData.dateOfBirth)
        }

        try {
            const shippingAddress = swMapCustomerAddress(formData.shippingAddress.value)
            const billingAddress = swMapCustomerAddress(formData.billingAddress.value)

            const requestBody = {
                ...(dobObject != null && { birthdayDay: dobObject.getDate() }),
                ...(dobObject != null && { birthdayMonth: dobObject.getMonth() + 1 }),
                ...(dobObject != null && { birthdayYear: dobObject.getFullYear() }),
                email: formData.email,
                password: formData.password,
                salutationId: formData.billingAddress.value.salutation,
                firstName: formData.billingAddress.value.firstName,
                lastName: formData.billingAddress.value.lastName,
                // @ts-ignore
                customFields: {
                    customer_referral_source: formData.customerReferralSource,
                    // @ts-ignore
                    customer_permission_deliverer_data: formData.improvedDeliveryService,
                    custom_customer_iban: formData.customerIban
                },
                acceptedDataProtection: true,
                // @ts-ignore
                storefrontUrl: import.meta.client ? window.location.origin : '',
                shippingAddress,
                billingAddress,
                guest: !formData.createAccount
            }

            if (formData.billingSameAsShipping) {
                Object.assign(requestBody, {
                    shippingAddress: null
                })
            }

            // TODO: patch api client
            // @ts-ignore
            const response = await LoginRegistrationShopware.register(requestBody)

            if (response.doubleOptInRegistration) {
                showNotification('Please confirm your registration by clicking on the link in the email we send you.', 'success')
            }

            // Refresh session data
            await getSession()

            loading.value = false
        } catch (e) {
            const error = e as any

            if (error.body[0]?.detail != null) {
                showNotification(error.body[0]?.detail, 'error', true)
            } else {
                showNotification(error, 'error', true)
            }
            loading.value = false
            error.value = e
            throw e
        }
    }

    async function registerConfirm (formData: { em: string, hash: string }): Promise<void> {
        loading.value = true

        try {
            await LoginRegistrationShopware.registerConfirm(formData)
            loading.value = false
        } catch (e) {
            loading.value = false
            throw e
        }
    }

    async function updateShippingAddress (shippingAddress: HblCustomerShippingAddress): Promise<HblCustomerShippingAddress | void> {
        const mappedAddress = await updateCustomerAddress(shippingAddress)

        if (error.value || customer?.value == null || mappedAddress == null) {
            return
        }

        customer.value.shippingAddress = mappedAddress

        return mappedAddress
    }

    async function updateBillingAddress (billingAddress: HblCustomerBillingAddress): Promise<HblCustomerBillingAddress | void> {
        const mappedAddress = await updateCustomerAddress(billingAddress)

        if (error.value || customer?.value == null || mappedAddress == null) {
            return
        }

        customer.value.billingAddress = mappedAddress

        return mappedAddress
    }

    async function getCustomerAddresses (): Promise<HblCustomerBillingAddress[] | HblCustomerShippingAddress[]> {
        loading.value = true
        error.value = false

        try {
            const response = await AddressShopware.listAddress()

            loading.value = false

            // Todo patch api client
            // @ts-ignore
            return hblMapCustomerAddresses(response.elements)
        } catch (e) {
            loading.value = false
            error.value = e
            throw e
        }
    }

    async function addCustomerAddress (address: HblCustomerBillingAddress | HblCustomerShippingAddress): Promise<HblCustomerBillingAddress | HblCustomerShippingAddress | void> {
        loading.value = true
        error.value = false

        try {
            const response = await AddressShopware.createCustomerAddress(
                swMapCustomerAddress(address)
            )

            loading.value = false
            return hblMapCustomerAddress(response)
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function updateCustomerAddress (address: HblCustomerBillingAddress | HblCustomerShippingAddress): Promise<HblCustomerBillingAddress | HblCustomerShippingAddress | void> {
        loading.value = true
        error.value = false

        try {
            const response = await AddressShopware.updateCustomerAddress(
                address.id,
                swMapCustomerAddress(address)
            )

            const mappedAddress = hblMapCustomerAddress(response)

            loading.value = false
            return mappedAddress
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function deleteCustomerAddress (addressId: string): Promise<void> {
        loading.value = true
        error.value = false

        try {
            await AddressShopware.deleteCustomerAddress(addressId)
            loading.value = false
            return
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function getOrders (params?: { id?: string, page?: number }): Promise<{ data: HblOrder | HblOrder[], total: number, page: number, limit: number }> {
        loading.value = true
        error.value = false

        let filter = null
        if (params?.id != null) {
            filter = [
                {
                    type: 'equals',
                    field: 'id',
                    value: params?.id
                }
            ]
        }

        try {
            const response = await OrderShopware.readOrder({
                limit: 10,
                'total-count-mode': 'next-pages',
                ...(params?.page != null && { page: params?.page }),
                associations: {
                    deliveries: {
                        associations: {
                            shippingMethod: {
                                associations: {
                                    prices: {}
                                }
                            },
                            shippingOrderAddress: {
                                associations: {
                                    salutation: {}
                                }
                            }
                        }
                    },
                    transactions: {
                        associations: {
                            paymentMethod: {}
                        }
                    },
                    lineItems: {
                        associations: {
                            cover: {},
                            downloads: {
                                associations: {
                                    media: {}
                                }
                            }
                        }
                    },
                    billingAddress: {
                        associations: {
                            salutation: {}
                        }
                    },
                    stateMachineState: {}
                },
                ...(filter != null && { filter })
            })

            const mappedData = {
                // @ts-ignore
                limit: response.orders?.limit,
                // @ts-ignore
                page: response.orders?.page,
                // @ts-ignore
                total: response.orders?.total,
                // @ts-ignore
                data: params?.id != null ? hblMapOrder(response.orders?.elements[0]) : hblMapOrders(response.orders?.elements)
            }

            loading.value = false
            return mappedData
        } catch (e) {
            loading.value = false
            error.value = e
            throw new Error(e as string)
        }
    }

    async function getOrderDocumentDownload (orderId: string, downloadId: string): Promise<any> {
        loading.value = true
        error.value = false

        try {
            const document = await DocumentShopware.download(orderId, downloadId)
            loading.value = false
            return document
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function getOrderLineItemDownload (orderId: string, downloadId: string): Promise<Blob | void> {
        loading.value = true
        error.value = false

        try {
            loading.value = false
            return await OrderShopware.orderDownloadFile(orderId, downloadId)
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function setDefaultShipping (id: string): Promise<void> {
        loading.value = true
        error.value = false

        try {
            await AddressShopware.defaultShippingAddress(id)
            loading.value = false
            return
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function requireNewPassword (email: string): Promise<void> {
        loading.value = true
        error.value = false

        try {
            await ProfileShopware.sendRecoveryMail({
                email,
                // @ts-ignore
                storefrontUrl: import.meta.client ? window.location.origin : '' as string
            })

            loading.value = false
            return
        } catch (e) {
            loading.value = false
            error.value = e
            throw e
        }
    }

    async function setNewPassword (hash: string, password: string, passwordRepeat: string): Promise<void> {
        loading.value = true
        error.value = false

        try {
            await ProfileShopware.recoveryPassword({
                hash,
                newPassword: password,
                newPasswordConfirm: passwordRepeat
            })

            loading.value = false
            return
        } catch (e) {
            loading.value = false
            error.value = e
            throw e
        }
    }

    async function editCustomerInfo (formData: { dateOfBirth: string, salutationId: string, firstName: string, lastName: string, improvedDeliveryService: boolean, customerIban: string, queryViaPhone?: boolean, permissionOutbound?: boolean }): Promise<void> {
        loading.value = true
        error.value = false

        try {
            let dateOfBirth = null
            if (formData.dateOfBirth != null) {
                dateOfBirth = new Date(formData.dateOfBirth)
            }

            await ProfileShopware.changeProfile({
                salutationId: formData.salutationId,
                firstName: formData.firstName,
                lastName: formData.lastName,
                // @ts-ignore
                customFields: Object.fromEntries(
                    Object.entries({
                        customer_referral_source: formData.customerReferralSource,
                        customer_permission_deliverer_data: formData.improvedDeliveryService,
                        custom_customer_iban: formData.customerIban,
                        customer_query_via_phone: formData.queryViaPhone,
                        customer_permission_outbound: formData.permissionOutbound
                    }).filter(([, v]) => v !== undefined)
                ),
                ...(dateOfBirth != null && {
                    birthdayDay: dateOfBirth.getDate(),
                    birthdayMonth: dateOfBirth.getMonth() + 1,
                    birthdayYear: dateOfBirth.getFullYear()
                })
            })

            loading.value = false
            return
        } catch (e) {
            loading.value = false
            error.value = e
            throw e
        }
    }

    async function editCustomerEmail (formData: { email: string, emailConfirmation: string, password: string }): Promise<void> {
        loading.value = true
        error.value = false

        try {
            await ProfileShopware.changeEmail(formData)

            loading.value = false
            return
        } catch (e) {
            loading.value = false
            error.value = e
            throw e
        }
    }

    async function editCustomerPassword (formData: { password: string, newPassword: string, newPasswordConfirm: string }): Promise<void> {
        loading.value = true
        error.value = false

        try {
            await ProfileShopware.changePassword(formData)

            loading.value = false
            return
        } catch (e) {
            loading.value = false
            error.value = e
            throw e
        }
    }

    async function readNewsletterStatus (): Promise<string> {
        const response = await __request(OpenAPI, {
            method: 'POST',
            url: '/account/newsletter-recipient-info',
        })

        // @ts-ignore
        return response
    }

    async function editCustomerNewsletter (formData: { email: string, option: string, storefrontUrl: string, frequency?: string }): Promise<void> {
        loading.value = true
        error.value = false

        if (formData.option === 'unsubscribe') {
            try {
                await NewsletterShopware.unsubscribeToNewsletter({ email: formData.email })

                loading.value = false
                return
            } catch (e) {
                loading.value = false
                error.value = e
                throw e
            }
        }

        try {
            await NewsletterShopware.subscribeToNewsletter({
                email: formData.email,
                option: formData.option,
                storefrontUrl: formData.storefrontUrl,
                // @ts-ignore
                salutationId: formData.salutationId,
                // @ts-ignore
                firstName: formData.firstName,
                // @ts-ignore
                lastName: formData.lastName,
                // @ts-ignore
                street: formData.street,
                // @ts-ignore
                zipCode: formData.zipCode,
                // @ts-ignore
                city: formData.city,
                // @ts-ignore
                customFields: {
                    lg_newsletter_subscriber_receiving_frequency: formData.frequency
                }
            })

            loading.value = false
            return
        } catch (e) {
            loading.value = false
            error.value = e
            throw e
        }
    }

    async function confirmCustomerNewsletter (formData: { em: string, hash: string }): Promise<void> {
        loading.value = true

        try {
            await NewsletterShopware.confirmNewsletter(formData)

            loading.value = false
        } catch (e) {
            loading.value = false
            throw e
        }
    }

    async function editCustomerPayment (paymentId: string): Promise<void> {
        loading.value = true
        error.value = false

        try {
            await ProfileShopware.changePaymentMethod(paymentId)

            loading.value = false
            return
        } catch (e) {
            loading.value = false
            error.value = e
            throw e
        }
    }

    return {
        customer,
        loading,
        error,
        getCustomer,
        login,
        logout,
        logoutGuest,
        register,
        registerConfirm,
        updateShippingAddress,
        updateBillingAddress,
        getCustomerAddresses,
        addCustomerAddress,
        updateCustomerAddress,
        deleteCustomerAddress,
        getOrders,
        getOrderLineItemDownload,
        getOrderDocumentDownload,
        setDefaultShipping,
        requireNewPassword,
        setNewPassword,
        editCustomerInfo,
        editCustomerEmail,
        editCustomerPassword,
        editCustomerNewsletter,
        confirmCustomerNewsletter,
        editCustomerPayment,
        readNewsletterStatus
    }
})
